<script setup>
// Legacy <woot-button> shim, restyled to the Atende design system.
// Keeps the old Chatwoot WootButton API (variant/color-scheme/size/icon/
// is-loading/is-disabled/is-expanded) — use components-next/button/Button.vue
// for new code.
import { computed, useSlots } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'button',
  },
  variant: {
    type: String,
    default: '',
  },
  colorScheme: {
    type: String,
    default: 'primary',
  },
  size: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: '',
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isExpanded: {
    type: Boolean,
    default: false,
  },
  classNames: {
    type: [String, Object, Array],
    default: '',
  },
});

const emit = defineEmits(['click']);

const slots = useSlots();

const STYLES = {
  primary: {
    solid: 'bg-n-blue-9 text-white hover:enabled:bg-n-blue-10',
    smooth: 'bg-n-brand/10 text-n-blue-11 hover:enabled:bg-n-brand/20',
    hollow:
      'outline outline-1 outline-offset-[-1px] outline-n-strong text-n-slate-11 hover:enabled:bg-n-slate-2',
    clear: 'text-n-blue-11 hover:enabled:bg-n-alpha-2',
    link: 'text-n-blue-11 hover:enabled:underline',
  },
  secondary: {
    solid: 'bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20',
    smooth: 'bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20',
    hollow:
      'outline outline-1 outline-offset-[-1px] outline-n-strong text-n-slate-11 hover:enabled:bg-n-slate-2',
    clear: 'text-n-slate-11 hover:enabled:bg-n-alpha-2',
    link: 'text-n-slate-11 hover:enabled:underline',
  },
  alert: {
    solid: 'bg-n-ruby-9 text-white hover:enabled:bg-n-ruby-10',
    smooth: 'bg-n-ruby-9/10 text-n-ruby-11 hover:enabled:bg-n-ruby-9/20',
    hollow:
      'outline outline-1 outline-offset-[-1px] outline-n-ruby-8 text-n-ruby-11 hover:enabled:bg-n-ruby-9/10',
    clear: 'text-n-ruby-11 hover:enabled:bg-n-ruby-9/10',
    link: 'text-n-ruby-11 hover:enabled:underline',
  },
  success: {
    solid: 'bg-n-teal-9 text-white hover:enabled:bg-n-teal-10',
    smooth: 'bg-n-teal-9/10 text-n-teal-11 hover:enabled:bg-n-teal-9/20',
    hollow:
      'outline outline-1 outline-offset-[-1px] outline-n-teal-9 text-n-teal-11 hover:enabled:bg-n-teal-9/10',
    clear: 'text-n-teal-11 hover:enabled:bg-n-teal-9/10',
    link: 'text-n-teal-11 hover:enabled:underline',
  },
  warning: {
    solid: 'bg-n-amber-9 text-white hover:enabled:bg-n-amber-10',
    smooth: 'bg-n-amber-9/10 text-n-slate-12 hover:enabled:bg-n-amber-9/20',
    hollow:
      'outline outline-1 outline-offset-[-1px] outline-n-amber-9 text-n-amber-11 hover:enabled:bg-n-amber-9/10',
    clear: 'text-n-amber-11 hover:enabled:bg-n-amber-9/10',
    link: 'text-n-amber-11 hover:enabled:underline',
  },
};

const hasOnlyIcon = computed(() => Boolean(props.icon) && !slots.default);

const sizeClasses = computed(() => {
  const sizeMap = {
    tiny: hasOnlyIcon.value ? 'h-6 w-6' : 'h-6 px-2 text-xs',
    small: hasOnlyIcon.value ? 'h-8 w-8' : 'h-8 px-3 text-xs',
    large: hasOnlyIcon.value ? 'h-12 w-12' : 'h-12 px-5 text-sm',
  };
  return (
    sizeMap[props.size] ||
    (hasOnlyIcon.value ? 'h-10 w-10' : 'h-10 px-4 text-sm')
  );
});

const variantClasses = computed(() => {
  const scheme = STYLES[props.colorScheme] || STYLES.primary;
  return scheme[props.variant] || scheme.solid;
});

const iconSize = computed(() =>
  ['tiny', 'small'].includes(props.size) ? 14 : 16
);

const handleClick = event => {
  if (props.isDisabled || props.isLoading) return;
  emit('click', event);
};
</script>

<template>
  <button
    :type="type"
    :disabled="isDisabled || isLoading"
    class="inline-flex items-center justify-center flex-shrink-0 gap-2 rounded-lg font-medium transition-all duration-100 ease-out border-0 select-none disabled:cursor-not-allowed disabled:opacity-50"
    :class="[variantClasses, sizeClasses, { 'w-full': isExpanded }, classNames]"
    @click="handleClick"
  >
    <Spinner v-if="isLoading" size="small" />
    <fluent-icon v-else-if="icon" :icon="icon" :size="iconSize" />
    <slot />
  </button>
</template>
